import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';

import { AuthService } from '../services/auth.service';
import { retryAfterSeconds } from '../services/newsletter.service';
import { ButtonComponent } from '../ui/button/button.component';
import { PLAYERS_RESEND_COOLDOWN_S } from './players.constants';
import { PLAYERS_COPY } from './players.copy';
import { serverMessage } from './players.utils';

/**
 * «Reenviar correo de verificación» del directorio (D10) y de la pestaña del perfil
 * (D22): una sola copia. Tras cada intento el botón espera 60 s, o `Retry-After` si es
 * mayor, sin cuenta atrás visible (recorte 2). El 429 pinta el texto del servidor; si no
 * llega, el de B10.
 */
@Component({
  selector: 'app-players-resend-verification',
  imports: [ButtonComponent],
  template: `
    <app-button
      variant="secondary"
      [loading]="busy()"
      [disabled]="coolingDown()"
      (click)="resend()"
    >
      {{ copy.resend }}
    </app-button>
    @if (notice(); as n) {
      <p class="players-resend__notice" [class.players-resend__notice--error]="n.tone === 'error'" role="status">
        {{ n.text }}
      </p>
    }
  `,
  styles: `
    :host {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: var(--space-3);
    }
    .players-resend__notice {
      margin: 0;
      font-family: var(--font-family-body);
      font-size: var(--text-size-sm);
      color: var(--color-text-secondary);
      text-align: center;
    }
    .players-resend__notice--error {
      color: var(--color-state-error);
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlayersResendVerificationComponent {
  private readonly auth = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly copy = PLAYERS_COPY.directory;
  protected readonly busy = signal(false);
  protected readonly coolingDown = signal(false);
  protected readonly notice = signal<{ tone: 'info' | 'error'; text: string } | null>(null);
  private timer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    this.destroyRef.onDestroy(() => this.clearTimer());
  }

  protected resend(): void {
    const user = this.auth.currentUser();
    if (!user || this.busy() || this.coolingDown()) return;
    this.busy.set(true);
    this.notice.set(null);
    this.auth
      .resendVerificationEmail(user.id)
      .pipe(
        finalize(() => this.busy.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.notice.set({ tone: 'info', text: this.copy.resendSent });
          this.startCooldown(PLAYERS_RESEND_COOLDOWN_S);
        },
        error: (err: unknown) => {
          if (err instanceof HttpErrorResponse && err.status === 429) {
            this.notice.set({
              tone: 'error',
              text: serverMessage(err) ?? this.copy.resendRateLimitedFallback,
            });
            const seconds = retryAfterSeconds(err.headers.get('Retry-After')) ?? 0;
            this.startCooldown(Math.max(PLAYERS_RESEND_COOLDOWN_S, seconds));
            return;
          }
          this.notice.set({ tone: 'error', text: this.copy.resendError });
        },
      });
  }

  private startCooldown(seconds: number): void {
    this.clearTimer();
    this.coolingDown.set(true);
    this.timer = setTimeout(() => {
      this.timer = null;
      this.coolingDown.set(false);
    }, seconds * 1000);
  }

  private clearTimer(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }
}
